import React, { useState, useEffect } from 'react';
import {
  Modal,
  Table,
  Typography,
  Spin,
  message,
  Tag,
  Divider,
  Row,
  Col,
  Card,
  Space
} from 'antd';
import { CalendarOutlined, UserOutlined, ShoppingCartOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { inventoryAPI, customerOrderAPI } from '../services/api.ts';
import { OrderCompleteness } from '../types.ts';

const { Title, Text } = Typography;

const generateCompositeIdentifier = (internalCode?: string, size?: string, color?: string): string => {
  if (!internalCode) return '';
  
  const sizePartTrimmed = size?.trim() || '';
  const colorPartTrimmed = color?.trim() || '';
  
  if (!sizePartTrimmed && !colorPartTrimmed) {
    return internalCode; // ABC123
  } else if (sizePartTrimmed && !colorPartTrimmed) {
    return `${internalCode}-${sizePartTrimmed}`; // ABC123-L
  } else if (!sizePartTrimmed && colorPartTrimmed) {
    return `${internalCode}--${colorPartTrimmed}`; // ABC123--RED
  } else {
    return `${internalCode}-${sizePartTrimmed}-${colorPartTrimmed}`; // ABC123-L-RED
  }
};

interface StockReservation {
  cart_id: number;
  quantity_reserved: number;
  reserved_at: string;
  source_type: string;
  source_type_display: string;
  expected_availability_date: string;
  supplier_order_number?: string;
  supplier_order_status?: string;
}

interface OrderReservation {
  customer_order_id: number;
  customer_order_number: string;
  customer_order_number_external?: string;
  customer_company: string;
  customer_code: string;
  order_date: string;
  requested_delivery_date?: string;
  customer_order_status: string;
  reservations: StockReservation[];
  total_reserved: number;
  max_availability_date?: string;
}

interface ReservationsSummary {
  total_orders_with_reservations: number;
  total_reserved_quantity: number;
  stock_info: {
    id: number;
    stock_code?: string;
    qta_available: number;
    qta_reserved: number;
    qta_in_production: number;
    internal_product_code?: string;
    size?: string;
    color?: string;
    product_description?: string;
  };
}

interface StockReservationsData {
  summary: ReservationsSummary;
  orders: OrderReservation[];
}

interface StockReservationsModalProps {
  visible: boolean;
  stockId: number | null;
  stockCode?: string;
  productDescription?: string;
  onClose: () => void;
}

const StockReservationsModal: React.FC<StockReservationsModalProps> = ({
  visible,
  stockId,
  stockCode,
  productDescription,
  onClose
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<StockReservationsData | null>(null);
  const [orderCompleteness, setOrderCompleteness] = useState<{[key: number]: OrderCompleteness}>({});

  const loadReservations = async () => {
    if (!stockId) return;
    
    setLoading(true);
    try {
      const response = await inventoryAPI.getStockReservations(stockId);
      setData(response);
      
      // Carica i dati di completeness per ogni ordine
      if (response.orders && response.orders.length > 0) {
        loadOrdersCompleteness(response.orders);
      }
    } catch (error) {
      console.error('Error loading stock reservations:', error);
      message.error('Errore nel caricamento delle prenotazioni');
    } finally {
      setLoading(false);
    }
  };

  const loadOrdersCompleteness = async (orders: OrderReservation[]) => {
    try {
      const completenessPromises = orders.map(order => 
        customerOrderAPI.checkOrderCompleteness(order.customer_order_id)
          .then(data => ({ orderId: order.customer_order_id, data }))
          .catch(error => {
            console.error(`Error loading completeness for order ${order.customer_order_id}:`, error);
            return null;
          })
      );
      
      const results = await Promise.all(completenessPromises);
      const completenessMap: {[key: number]: OrderCompleteness} = {};
      
      results.forEach(result => {
        if (result) {
          completenessMap[result.orderId] = result.data;
        }
      });
      
      setOrderCompleteness(completenessMap);
    } catch (error) {
      console.error('Error loading orders completeness:', error);
    }
  };

  useEffect(() => {
    if (visible && stockId) {
      loadReservations();
    }
  }, [visible, stockId]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    if (dateString === 'Disponibile') return dateString;
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('it-IT');
    } catch {
      return dateString;
    }
  };

  const getSourceTypeColor = (sourceType: string) => {
    switch (sourceType) {
      case 'available_stock': return 'green';
      case 'in_transit': return 'orange';
      case 'in_production': return 'blue';
      default: return 'default';
    }
  };

  const renderOrderRow = (order: OrderReservation, index: number) => {
    const columns = [
      {
        title: 'Fonte',
        dataIndex: 'source_type_display',
        key: 'source_type_display',
        width: 120,
        render: (text: string, record: StockReservation) => (
          <Tag color={getSourceTypeColor(record.source_type)}>
            {text}
          </Tag>
        )
      },
      {
        title: 'Qta Riservata',
        dataIndex: 'quantity_reserved',
        key: 'quantity_reserved',
        width: 100,
        align: 'center' as const,
        render: (value: number) => <strong>{value}</strong>
      },
      {
        title: 'Data Disponibilità',
        dataIndex: 'expected_availability_date',
        key: 'expected_availability_date',
        width: 120,
        render: (date: string) => formatDate(date)
      },
      {
        title: 'Ordine Fornitore',
        key: 'supplier_info',
        width: 200,
        render: (record: StockReservation) => (
          record.supplier_order_number ? (
            <div>
              <div>{record.supplier_order_number}</div>
              {record.supplier_order_status && (
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {record.supplier_order_status}
                </Text>
              )}
            </div>
          ) : '-'
        )
      },
      {
        title: 'Riservato il',
        dataIndex: 'reserved_at',
        key: 'reserved_at',
        width: 120,
        render: (date: string) => formatDate(date)
      }
    ];

    return (
      <Card 
        key={order.customer_order_id}
        style={{ marginBottom: '16px' }}
        size="small"
      >
        {/* Header Ordine */}
        <Row gutter={[16, 8]} style={{ marginBottom: '12px' }}>
          <Col span={24}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Row justify="space-between" align="middle">
                <Col>
                  <Space>
                    <ShoppingCartOutlined />
                    <strong>{order.customer_order_number}</strong>
                    {order.customer_order_number_external && (
                      <span> - {order.customer_order_number_external}</span>
                    )}
                  </Space>
                </Col>
                <Col>
                  <Space>
                    <Tag color="blue">
                      Totale: {order.total_reserved}
                    </Tag>
                    {(() => {
                      const completeness = orderCompleteness[order.customer_order_id];
                      if (!completeness) {
                        return <Tag color="default">Verifica...</Tag>;
                      }
                      
                      if (completeness.can_fulfill) {
                        return (
                          <Tag color="green" icon={<CheckCircleOutlined />}>
                            PRONTO
                          </Tag>
                        );
                      } else {
                        const percentage = Math.round(completeness.total_completion_percentage);
                        return (
                          <Tag color="orange">
                            {percentage}%
                          </Tag>
                        );
                      }
                    })()}
                  </Space>
                </Col>
              </Row>
              
              <Row gutter={[16, 4]}>
                <Col span={8}>
                  <Space size="small">
                    <UserOutlined />
                    <span>
                      <strong>{order.customer_company}</strong>
                      {order.customer_code && ` (${order.customer_code})`}
                    </span>
                  </Space>
                </Col>
                <Col span={8}>
                  <Space size="small">
                    <CalendarOutlined />
                    <span>Ordine: {formatDate(order.order_date)}</span>
                  </Space>
                </Col>
                <Col span={8}>
                  {order.requested_delivery_date && (
                    <Space size="small">
                      <CalendarOutlined />
                      <span>Consegna: {formatDate(order.requested_delivery_date)}</span>
                    </Space>
                  )}
                </Col>
              </Row>

              {order.max_availability_date && (
                <Row>
                  <Col span={24}>
                    <Text strong style={{ color: '#1890ff' }}>
                      Data possibile evasione: {formatDate(order.max_availability_date)}
                    </Text>
                  </Col>
                </Row>
              )}
            </Space>
          </Col>
        </Row>

        {/* Tabella Prenotazioni */}
        <Table 
          columns={columns}
          dataSource={order.reservations}
          rowKey="cart_id"
          size="small"
          pagination={false}
          bordered
        />
      </Card>
    );
  };

  // Genera l'identificativo composto e la descrizione del prodotto
  const getProductDisplayInfo = () => {
    if (data?.summary?.stock_info) {
      const stockInfo = data.summary.stock_info;
      const compositeId = generateCompositeIdentifier(
        stockInfo.internal_product_code,
        stockInfo.size,
        stockInfo.color
      );
      
      if (compositeId && stockInfo.product_description) {
        return `${compositeId} - ${stockInfo.product_description}`;
      } else if (compositeId) {
        return compositeId;
      } else if (stockInfo.product_description) {
        return stockInfo.product_description;
      }
    }
    
    // Fallback per retrocompatibilità
    if (stockCode) {
      return `Stock: ${stockCode}${productDescription ? ` - ${productDescription}` : ''}`;
    }
    
    return null;
  };

  return (
    <Modal
      title={
        <div>
          <Title level={4} style={{ margin: 0 }}>
            Dettagli Prenotazioni Stock
          </Title>
          {getProductDisplayInfo() && (
            <Text type="secondary">
              {getProductDisplayInfo()}
            </Text>
          )}
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width="90%"
      style={{ maxWidth: '1200px' }}
    >
      <Spin spinning={loading}>
        {data && (
          <>
            {/* Riepilogo */}
            <Card size="small" style={{ marginBottom: '16px', backgroundColor: '#f0f2f5' }}>
              <Row gutter={[16, 8]}>
                <Col span={6}>
                  <Text strong>Stock Code: </Text>
                  <br />
                  <Text>{data.summary.stock_info.stock_code || 'N/A'}</Text>
                </Col>
                <Col span={6}>
                  <Text strong>Disponibile: </Text>
                  <br />
                  <Tag color="green">{data.summary.stock_info.qta_available}</Tag>
                </Col>
                <Col span={6}>
                  <Text strong>Riservato: </Text>
                  <br />
                  <Tag color="orange">{data.summary.stock_info.qta_reserved}</Tag>
                </Col>
                <Col span={6}>
                  <Text strong>In Produzione: </Text>
                  <br />
                  <Tag color="blue">{data.summary.stock_info.qta_in_production}</Tag>
                </Col>
              </Row>
              <Divider style={{ margin: '12px 0' }} />
              <Row gutter={[16, 8]}>
                <Col span={12}>
                  <Text strong>Ordini con Prenotazioni: </Text>
                  <Tag color="purple">{data.summary.total_orders_with_reservations}</Tag>
                </Col>
                <Col span={12}>
                  <Text strong>Quantità Totale Riservata: </Text>
                  <Tag color="red">{data.summary.total_reserved_quantity}</Tag>
                </Col>
              </Row>
            </Card>

            {/* Lista Ordini */}
            <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {data.orders.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Text type="secondary">Nessuna prenotazione trovata per questo stock</Text>
                </div>
              ) : (
                data.orders.map((order, index) => renderOrderRow(order, index))
              )}
            </div>
          </>
        )}
      </Spin>
    </Modal>
  );
};

export default StockReservationsModal;